'use client'

import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect
} from 'react'

import { useQueryClient } from '@tanstack/react-query'

import {
  getLevelPlayState,
  type LevelPlayState
} from '../domain/getLevelPlayState'
import { getSteps } from '../domain/getSteps'
import { isChallengeDoneError } from '../domain/isChallengeDoneError'
import { resolveResumeStepIndex } from '../domain/resolveResumeStepIndex'
import {
  finishChallenge,
  getLevelRecordPath
} from '../services/level-play.api'

import { isRedoBlocked } from '@/features/journeys'
import type { Challenge, Level } from '@/features/journeys'
import { useUserProgress } from '@/shared/contexts/UserProgress'
import { useLevelProgress } from '@/shared/hooks/useLevelProgress'
import { useOptimizedRecord } from '@/shared/hooks/useOptimizedQuery'
import { sortByPosition } from '@/shared/lib/sort'
import { FlowProvider } from '@/shared/lib/steps-flow'
import {
  MINIMUM_ADVANCE_CONTENT_KINDS,
  type MinimumAdvanceContentKind
} from '@/shared/lib/steps-flow/types'
import { LevelProgress } from '@/shared/types/user-progress'

type OnChallengeAnswerParams = {
  uid: string
  answer: string
  voucherId?: string
} & ({ answerIsCorrect: boolean } | { skipValidation: boolean })

interface LevelContextValue {
  levelUid: string
  level: Level | null
  levelProgress: LevelProgress | null
  challenges: Challenge[]
  levelPlayState: LevelPlayState
  isLevelFetching: boolean
  stepsCount: number
  /** Step to open when the student continues a level they already started. */
  resumeStepIndex: number
  isSubmitting: boolean
  journeyAllowWrongAnswers: boolean
  getMinimumAdvanceStatus: (challengeId: number) => {
    isEnabled: boolean
    isLocked: boolean
    remainingSeconds: number
    label: string
  }
  getMergedStoryRunMinimumAdvanceStatus: (challengeIds: number[]) => {
    isEnabled: boolean
    isLocked: boolean
    remainingSeconds: number
    label: string
  }
  onChallengeAnswer: (params: OnChallengeAnswerParams) => Promise<{
    ok: boolean
  }>
}

const LevelContext = createContext<LevelContextValue | null>(null)

export function useLevelContext(): LevelContextValue {
  const ctx = useContext(LevelContext)
  if (!ctx) throw new Error('useLevelContext must be used within LevelProvider')
  return ctx
}

interface LevelProviderProps {
  levelUid: string
  children: ReactNode
}

export function LevelProvider({ levelUid, children }: LevelProviderProps) {
  const queryClient = useQueryClient()
  const levelPath = getLevelRecordPath(levelUid)
  const {
    record: level,
    isLoading: isLevelLoading,
    isFetching: isLevelFetching
  } = useOptimizedRecord<Level>({
    path: levelPath
  })
  // Order comes from the client, not from the API payload: getSteps sorts
  // internally, but the context also exposes `challenges` directly.
  const challenges = useMemo(
    () => [...(level?.active_challenges ?? [])].sort(sortByPosition),
    [level?.active_challenges]
  )

  const { levelProgress } = useLevelProgress({ id: level?.id })
  const { progress, levelWasFinished } = useUserProgress()
  const finishedChallengeIds = useMemo(
    () => new Set(progress?.finished_challenges ?? []),
    [progress?.finished_challenges]
  )

  const steps = useMemo(
    () =>
      getSteps(challenges, {
        includeInitial: true,
        includeFinishSummary: true,
        filterChallenge: ch =>
          !ch.conditional || ch.conditions_met !== false
      }),
    [challenges]
  )

  const journeyAllowWrongAnswers = level?.journey?.allow_wrong_answers ?? false

  // `useOptimizedRecord` devolve o registro anterior (placeholderData) durante
  // a troca de nível — só considere `level` quando ele é o da rota atual, senão
  // o veredito de redo-block é calculado com o nível recém-concluído.
  const isLevelCurrent = level?.uid === levelUid

  // Derived from the level record and UserProgress rather than `levelProgress`,
  // which stays null until the journeys store loads — on a deep link into a
  // level that would leave a window where a finished level renders as playable.
  const blockRedo = level?.journey?.block_redo ?? false
  const levelFinished = !!level && levelWasFinished(level.id)

  const computedRedoBlocked =
    isLevelCurrent &&
    isRedoBlocked({
      blockRedo,
      levelFinished,
      allChallengesFinished:
        challenges.length > 0 &&
        challenges.every(challenge => finishedChallengeIds.has(challenge.id))
    })

  // Precondição exata de ensure_redo_allowed! no lado do nível: com ela ligada,
  // qualquer desafio já concluído é rejeitado pela API. Vale mesmo quando o
  // nível segue jogável por ter ganhado um desafio depois de concluído — que é
  // justamente o estado em que o aluno ficava travado.
  const redoBlockedForFinishedChallenges = blockRedo && levelFinished

  // O bloqueio de refazer vale apenas na ENTRADA do nível. O veredito é
  // congelado assim que nível + progresso carregam: sem isso, concluir o
  // último desafio numa jornada com block_redo atualiza o progresso, o
  // bloqueio "liga" no meio da sessão e derruba o fluxo antes do resumo final.
  const entryRedoBlockedRef = useRef<{ levelUid: string; value: boolean } | null>(
    null
  )
  if (entryRedoBlockedRef.current?.levelUid !== levelUid) {
    entryRedoBlockedRef.current = null
  }
  if (!entryRedoBlockedRef.current && isLevelCurrent && progress) {
    entryRedoBlockedRef.current = { levelUid, value: computedRedoBlocked }
  }
  const redoBlocked = entryRedoBlockedRef.current?.value ?? computedRedoBlocked

  // Sem isto o fluxo reabre no passo 1 e reenvia desafios já concluídos — numa
  // jornada com block_redo a API responde 422 `challenge_done` e o aluno trava
  // na etapa, que é o bug relatado.
  const resumeStepIndex = useMemo(
    () => resolveResumeStepIndex({ steps, finishedChallengeIds }),
    [steps, finishedChallengeIds]
  )

  const levelPlayState = getLevelPlayState({
    // Registro stale durante a troca de nível conta como carregando.
    isLoading: isLevelLoading || (!!level && !isLevelCurrent),
    level,
    levelProgress,
    redoBlocked,
    stepsCount: steps.length
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)
  const minimumAdvanceStartedAtRef = useRef<Record<string, number>>({})
  const [minimumAdvanceNowMs, setMinimumAdvanceNowMs] = useState(() =>
    Date.now()
  )

  useEffect(() => {
    minimumAdvanceStartedAtRef.current = {}
    setMinimumAdvanceNowMs(Date.now())
  }, [levelUid])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setMinimumAdvanceNowMs(Date.now())
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [])

  const setSubmittingDeferred = useCallback((value: boolean) => {
    queueMicrotask(() => setIsSubmitting(value))
  }, [])

  const formatRemainingSeconds = useCallback((totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0'
    )}`
  }, [])

  const getMinimumAdvanceStatus = useCallback(
    (challengeId: number) => {
      const cacheKey = `c:${challengeId}`
      const challenge = challenges.find(item => item.id === challengeId)
      const minimumSeconds =
        challenge?.minimum_time_to_advance_in_seconds ?? 0
      const kind = challenge?.kind as MinimumAdvanceContentKind | undefined
      const isEligible =
        !!kind && MINIMUM_ADVANCE_CONTENT_KINDS.includes(kind)

      if (!challenge || !isEligible || minimumSeconds <= 0) {
        return {
          isEnabled: false,
          isLocked: false,
          remainingSeconds: 0,
          label: ''
        }
      }

      if (!minimumAdvanceStartedAtRef.current[cacheKey]) {
        minimumAdvanceStartedAtRef.current[cacheKey] = minimumAdvanceNowMs
      }

      const startedAt = minimumAdvanceStartedAtRef.current[cacheKey]
      const elapsedSeconds = Math.floor((minimumAdvanceNowMs - startedAt) / 1000)
      const remainingSeconds = Math.max(0, minimumSeconds - elapsedSeconds)

      return {
        isEnabled: true,
        isLocked: remainingSeconds > 0,
        remainingSeconds,
        label: formatRemainingSeconds(remainingSeconds)
      }
    },
    [challenges, formatRemainingSeconds, minimumAdvanceNowMs]
  )

  const getMergedStoryRunMinimumAdvanceStatus = useCallback(
    (challengeIds: number[]) => {
      const cacheKey = `merged-story:${challengeIds.join(':')}`
      const maxSeconds = Math.max(
        0,
        ...challengeIds.map(id => {
          const c = challenges.find(item => item.id === id)
          return c?.minimum_time_to_advance_in_seconds ?? 0
        })
      )
      const allStoriesKind = challengeIds.every(id => {
        const c = challenges.find(item => item.id === id)
        return c?.kind === 'stories'
      })

      if (!allStoriesKind || maxSeconds <= 0) {
        return {
          isEnabled: false,
          isLocked: false,
          remainingSeconds: 0,
          label: ''
        }
      }

      if (!minimumAdvanceStartedAtRef.current[cacheKey]) {
        minimumAdvanceStartedAtRef.current[cacheKey] = minimumAdvanceNowMs
      }

      const startedAt = minimumAdvanceStartedAtRef.current[cacheKey]
      const elapsedSeconds = Math.floor((minimumAdvanceNowMs - startedAt) / 1000)
      const remainingSeconds = Math.max(0, maxSeconds - elapsedSeconds)

      return {
        isEnabled: true,
        isLocked: remainingSeconds > 0,
        remainingSeconds,
        label: formatRemainingSeconds(remainingSeconds)
      }
    },
    [challenges, formatRemainingSeconds, minimumAdvanceNowMs]
  )

  const onChallengeAnswer = async (params: OnChallengeAnswerParams) => {
    const { uid, answer, voucherId } = params
    if (isSubmittingRef.current) return { ok: false }

    // Um desafio já concluído numa jornada com block_redo é rejeitado pela API
    // com `challenge_done` — e o Stories submete o run inteiro em sequência,
    // então um story já concluído na cabeça do run travaria os que vêm atrás.
    // Nada a enviar: o resultado que o chamador queria já vale.
    const challengeId = challenges.find(item => item.uid === uid)?.id
    const alreadyFinished =
      challengeId !== undefined && finishedChallengeIds.has(challengeId)
    if (redoBlockedForFinishedChallenges && alreadyFinished) {
      return { ok: true }
    }

    isSubmittingRef.current = true
    setSubmittingDeferred(true)

    let successfullyFinished = false
    const rightAnswer =
      ('answerIsCorrect' in params && params.answerIsCorrect) ||
      ('skipValidation' in params && params.skipValidation)
    // A rejeição do axios sobe intacta de propósito: `new Error(String(error))`
    // descartava `response.data.error_message`, e o aluno via só
    // "AxiosError: Request failed with status code 422" em vez do motivo que a
    // API mandou (level_locked, journey_locked, ...). Sem o objeto original,
    // getErrorMessage não tem como chegar na mensagem de negócio.
    try {
      await finishChallenge({ uid, answer, voucherId, rightAnswer })
      await queryClient.refetchQueries({ queryKey: [levelPath] })
      successfullyFinished = true
    } catch (error) {
      // Corrida contra o guard acima (progresso ainda não refletido no
      // UserProgress): o desafio já estava concluído, então seguir é o
      // desfecho correto — não um erro para mostrar ao aluno.
      if (!isChallengeDoneError(error)) throw error

      await queryClient.refetchQueries({ queryKey: [levelPath] })
      successfullyFinished = true
    } finally {
      isSubmittingRef.current = false
      setSubmittingDeferred(false)
    }

    return { ok: successfullyFinished }
  }

  const content = (
    <LevelContext.Provider
      value={{
        levelUid,
        level,
        challenges,
        levelProgress,
        levelPlayState,
        isLevelFetching,
        stepsCount: steps.length,
        resumeStepIndex,
        journeyAllowWrongAnswers,
        isSubmitting,
        getMinimumAdvanceStatus,
        getMergedStoryRunMinimumAdvanceStatus,
        onChallengeAnswer
      }}
    >
      {children}
    </LevelContext.Provider>
  )

  if (levelPlayState.kind !== 'ready') {
    return content
  }

  return (
    <FlowProvider
      flowKey={levelUid}
      steps={steps}
      initialLives={level?.max_attempts ?? 5}
      initialSeconds={120}
    >
      {content}
    </FlowProvider>
  )
}
